import React, { useCallback, useEffect, useState } from "react";
import { Link2, Download, Wand2, Loader2, CheckCircle2 } from "lucide-react";
import { getMatching, buildMatching, downloadMatching } from "../../lib/uploadsApi";

const TYPES = [["item_master", "SKUs"], ["branch_master", "Branches"], ["supplier_master", "Suppliers"]];
const BTN = "h-8 px-3 inline-flex items-center gap-1.5 border border-border rounded-sm text-[12px] hover:border-primary hover:text-primary transition-colors disabled:opacity-50";

// Console → Bronze (IT Head / COO): codes used in Silver that have no item / branch / supplier master entry yet.
export default function MatchingMasters({ onToast, onBuilt }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => getMatching().then(setD).catch(e => onToast(e.message)), [onToast]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const total = TYPES.reduce((n, [t]) => n + (d.missing[t] || 0), 0);
  const build = () => {
    setBusy(true);
    buildMatching().then(r => { onToast(`${r.items.length} matching list(s) added to Bronze with sample names — review and promote them as usual.`); load(); onBuilt(); })
      .catch(e => onToast(e.message)).finally(() => setBusy(false));
  };
  return (
    <div className="tile p-4 space-y-3" data-testid="matching-masters">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-display font-semibold text-[14px] inline-flex items-center gap-2"><Link2 size={14} className="text-primary" /> Matching master lists</div>
          <p className="text-[12px] text-muted mt-0.5 max-w-[620px]">
            {total ? "Codes used in Silver sales, stock, receipts, POs or transfers that have no item, branch or supplier master entry yet. Download a pre-filled list to add real names, or add sample-named lists to Bronze."
              : "Every SKU, branch and supplier code used in Silver has a master entry."}
          </p>
        </div>
        {total === 0 && <span data-testid="matching-masters-complete" className="inline-flex items-center gap-1.5 text-[12px] text-success"><CheckCircle2 size={13} /> All matched</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {TYPES.map(([t, label]) => (
          <button key={t} type="button" data-testid={`matching-download-${t}`} onClick={() => downloadMatching(t).catch(e => onToast(e.message))} disabled={!d.missing[t]} className={BTN}>
            <Download size={12} /> {label} <span className="font-mono-tabular text-muted" data-testid={`matching-missing-${t}`}>{d.missing[t] || 0}</span>
          </button>
        ))}
        {total > 0 && (
          <button type="button" data-testid="matching-build" onClick={build} disabled={busy || d.waiting.length > 0}
                  className="h-8 px-3 inline-flex items-center gap-1.5 bg-primary text-primary-fg rounded-sm text-[12px] font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} Add sample-named lists to Bronze
          </button>
        )}
        {d.waiting.length > 0 && <span className="text-[11.5px] text-muted" data-testid="matching-waiting">{d.waiting.length} list(s) waiting for review in Bronze</span>}
      </div>
    </div>
  );
}
